import { Injectable } from '@angular/core'
import { combineLatest, Observable, of } from 'rxjs'
import {
  AbstractControl,
  AsyncValidatorFn,
  FormArray,
  FormControl,
  FormGroup,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms'
import { debounceTime, map, startWith } from 'rxjs/operators'

export interface DirtyTree {
  [key: string]: true | DirtyTree
}

export type FormArrayItemValue<T> =
  T extends FormArray<FormGroup<infer TControls>>
    ? {
        [K in keyof TControls]: TControls[K] extends FormControl<infer TValue> ? TValue : never
      }
    : never

@Injectable({
  providedIn: 'root',
})
export class FormsUtilsService {
  static RGX_TEXT = new RegExp('^\\D*$')
  static RGX_EMAIL = new RegExp('^.*@.*$', 'm')
  static RGX_POSTCODE = new RegExp('[0-9]{5}') // <-------It doesn't work good
  static RGX_COUNTRY_CODE = new RegExp(/^\d{1,3}$/)
  static RGX_PHONE_NUMBER = new RegExp(/^\d{9,10}$/)

  constructor() {}

  public static markFormGroupDirty(formGroup: FormGroup) {
    ;(Object as any).values(formGroup.controls).forEach((control) => {
      control.markAsDirty()
    })
  }

  public static markFormGroupTouched(formGroup: FormGroup) {
    ;(Object as any).values(formGroup.controls).forEach((control: UntypedFormControl) => {
      control.markAsTouched()
      control.updateValueAndValidity()
    })
  }

  public static getChangedPropertiesTree(form: AbstractControl): DirtyTree {
    const walk = (control: AbstractControl): true | DirtyTree | undefined => {
      if (control instanceof FormControl) {
        return control.dirty ? true : undefined
      }

      if (control instanceof FormGroup) {
        const result: DirtyTree = {}

        Object.entries(control.controls).forEach(([key, child]) => {
          const childResult = walk(child)
          if (childResult !== undefined) {
            result[key] = childResult
          }
        })

        return Object.keys(result).length > 0 ? result : undefined
      }

      if (control instanceof FormArray) {
        const result: DirtyTree = {}

        control.controls.forEach((child, index) => {
          const childResult = walk(child)
          if (childResult !== undefined) {
            result[String(index)] = childResult
          }
        })

        return Object.keys(result).length > 0 ? result : undefined
      }

      return undefined
    }

    return (walk(form) ?? {}) as DirtyTree
  }

  /**
   * Recursively picks only the values from the provided object that are marked as dirty in the dirty tree.
   *
   * @param value - The object or array to pick values from.
   * @param dirtyTree - The tree structure indicating which fields are dirty.
   * @return An object containing only the dirty values, preserving structure.
   */
  public static pickDirtyValues(value: any, dirtyTree: DirtyTree | true): any {
    if (dirtyTree === true) {
      return value
    }

    if (Array.isArray(value)) {
      return Object.entries(dirtyTree).reduce((acc, [index, childTree]) => {
        const childValue = value[Number(index)]
        if (childValue !== undefined) {
          acc.push(this.pickDirtyValues(childValue, childTree as DirtyTree))
        }
        return acc
      }, [] as any[])
    }

    // Specially handle Date so it isn't part of 'object'
    if (value instanceof Date) {
      return value
    }

    if (value !== null && typeof value === 'object') {
      const picked = Object.entries(dirtyTree).reduce(
        (acc, [key, childTree]) => {
          const childValue = value[key]
          if (childValue !== undefined) {
            acc[key] = this.pickDirtyValues(childValue, childTree as DirtyTree)
          }
          return acc
        },
        {} as Record<string, any>,
      )

      // Specially handle id field so it is always picked (even if not dirty)
      if ('id' in value && picked.id === undefined) {
        picked.id = value.id
      }

      return picked
    }

    return value
  }

  public static getChangedPropertiesForNestedForms<T extends FormGroup>(form: T): T['value'] {
    function extractChangedValues<U extends FormGroup | FormArray>(group: U): U['value'] {
      return Object.keys(group.controls).reduce(
        (acc, key) => {
          const control = group.controls[key]

          if (control instanceof FormGroup || control instanceof FormArray) {
            const nestedChanges = extractChangedValues(control)
            if (Object.keys(nestedChanges).length > 0) {
              ;(acc as any)[key] = nestedChanges
            }
          } else if (control.dirty) {
            ;(acc as any)[key] = control.value
          }
          return acc
        },
        {} as Partial<U['value']>,
      )
    }

    return extractChangedValues(form)
  }

  public static getChangedPropertyNames<T extends FormGroup>(form: T): string[] {
    return Object.keys(form.controls).filter((name) => form.controls[name].dirty)
  }

  public static getChangedProperties<T extends FormGroup>(form: T) {
    const res = {}
    this.getChangedPropertyNames(form).map((key) => {
      res[key] = form.getRawValue()[key]
    })
    return res
  }

  public static debounce<F extends (...args: any[]) => any>(func: F, waitFor: number) {
    let timeout

    return (...args: Parameters<F>): Promise<ReturnType<F>> =>
      new Promise((resolve) => {
        if (timeout) {
          clearTimeout(timeout)
        }

        timeout = setTimeout(() => resolve(func(...args)), waitFor)
      })
  }

  public static throttle = <F extends (...args: any[]) => any>(func: F, waitFor: number) => {
    const now = () => new Date().getTime()
    const resetStartTime = () => (startTime = now())
    let timeout
    let startTime: number = now() - waitFor

    return (...args: Parameters<F>): Promise<ReturnType<F>> =>
      new Promise((resolve) => {
        const timeLeft = startTime + waitFor - now()
        if (timeout) {
          clearTimeout(timeout)
        }
        if (startTime + waitFor <= now()) {
          resetStartTime()
          resolve(func(...args))
        } else {
          timeout = setTimeout(() => {
            resetStartTime()
            resolve(func(...args))
          }, timeLeft)
        }
      })
  }

  /**
   * A custom validator function to conditionally apply or remove validators
   * on a specific form control based on a given condition.
   *
   * @param inputName - The name of the target input control on which validators will be applied or removed.
   * @param condition - A function that determines whether the condition for applying validators is met.
   *                     It takes the parent form group as input and returns a boolean value.
   * @param validators - A single validator or an array of validators to be conditionally applied to the target input.
   *
   * @returns A `ValidatorFn` function that can be applied to a form group to dynamically manage validators
   *          on the specified input control based on the condition.
   *
   * ## Notes:
   * - Ensure `updateValueAndValidity` is called to immediately trigger validation status updates after changes.
   * - This function is designed to work with Angular reactive forms.
   */
  public static conditionalValidators =
    (
      inputName: string,
      condition: (form: AbstractControl) => boolean,
      validators: ValidatorFn[] | ValidatorFn,
    ): ValidatorFn =>
    (form: AbstractControl): ValidationErrors | null => {
      const targetInput = form.get(inputName)
      if (targetInput) {
        const meetsCondition = condition(form)
        const validatorArray = Array.isArray(validators) ? validators : [validators]
        let validatorsChanged = false
        validatorArray.forEach((validator) => {
          if (meetsCondition !== targetInput.hasValidator(validator)) {
            if (meetsCondition) {
              targetInput.addValidators(validator)
            } else {
              targetInput.removeValidators(validator)
            }
            validatorsChanged = true
          }
        })
        // Only call updateValueAndValidity if validators actually changed
        // and use { emitEvent: false } to prevent triggering parent validation
        if (validatorsChanged) {
          targetInput.updateValueAndValidity({ emitEvent: false })
        }
      }
      return null
    }

  /**
   * Async version of conditionalValidators()
   */
  public static conditionalAsyncValidators =
    (
      inputName: string,
      condition: (form: AbstractControl) => boolean,
      validators: AsyncValidatorFn[] | AsyncValidatorFn,
    ): AsyncValidatorFn =>
    (form: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> => {
      const targetInput = form.get(inputName)
      if (targetInput) {
        const meetsCondition = condition(form)
        const validatorArray = Array.isArray(validators) ? validators : [validators]
        let validatorsChanged = false
        validatorArray.forEach((validator) => {
          if (meetsCondition !== targetInput.hasAsyncValidator(validator)) {
            if (meetsCondition) {
              targetInput.addAsyncValidators(validator)
            } else {
              targetInput.removeAsyncValidators(validator)
            }
            validatorsChanged = true
          }
        })
        // Only call updateValueAndValidity if validators actually changed
        // and use { emitEvent: false } to prevent triggering parent validation
        if (validatorsChanged) {
          targetInput.updateValueAndValidity({ emitEvent: false })
        }
      }
      return of(null)
    }

  /**
   * Get human-readable names of invalidly filled fields. It will display only control fields not array or group names.
   * @param form from which form will information be extracted
   * @param fieldNames object with prepared human-readable names
   */
  public static getInvalidControls(form: AbstractControl, fieldNames: any) {
    const invalidControls: string[] = []

    if (form instanceof FormGroup) {
      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key)
        if (control?.invalid) {
          if (control instanceof FormGroup || control instanceof FormArray) {
            // Recursively get invalid controls for nested FormGroup
            invalidControls.push(...this.getInvalidControls(control, fieldNames))
          } else {
            invalidControls.push(fieldNames[key]) // Add the control name
          }
        }
      })
    }

    return invalidControls
  }

  /**
   * Get human-readable names of still fields in pending state. It will display only control fields not array or group names.
   * @param form from which form will information be extracted
   * @param fieldNames object with prepared human-readable names
   */
  public static getPendingControls(form: AbstractControl, fieldNames: any) {
    const pendingControls: string[] = []

    if (form instanceof FormGroup) {
      Object.keys(form.controls).forEach((key) => {
        const control = form.get(key)
        if (control?.pending) {
          if (control instanceof FormGroup || control instanceof FormArray) {
            // Recursively get invalid controls for nested FormGroup
            pendingControls.push(...this.getPendingControls(control, fieldNames))
          } else {
            pendingControls.push(fieldNames[key]) // Add the control name
          }
        }
      })
    }

    return pendingControls
  }

  static hasAnyInvalidControl<T extends { [K in keyof T]: AbstractControl<any, any, any> }>(
    array: FormArray<FormGroup<T>>,
    controlName: keyof T,
    triedToSubmit: boolean,
  ): boolean {
    return array.controls.some((row) => {
      const control = row.controls[controlName as keyof typeof row.controls]
      return (control?.invalid ?? false) && (control.touched || control.dirty || triedToSubmit)
    })
  }

  static observeAnyInvalidControl<T extends { [K in keyof T]: AbstractControl<any, any, any> }>(
    array: FormArray<FormGroup<T>>,
    controlName: keyof T,
    triedToSubmit$: Observable<boolean>,
  ): Observable<boolean> {
    return combineLatest([array.statusChanges.pipe(startWith(array.status)), triedToSubmit$]).pipe(
      debounceTime(300),
      map(([, triedToSubmit]) => FormsUtilsService.hasAnyInvalidControl(array, controlName, triedToSubmit)),
    )
  }

  static observeInvalidFieldsControls<T>(
    form: FormGroup,
    fields: (keyof T)[],
    triedToSubmit$: Observable<boolean>,
  ): Observable<boolean> {
    return combineLatest([
      combineLatest(
        fields.map((field) =>
          form.controls[field as string].statusChanges.pipe(startWith(form.controls[field as string].status)),
        ),
      ),
      triedToSubmit$,
    ]).pipe(
      debounceTime(300),
      map(([, triedToSubmit]) =>
        fields.some((field) => {
          const abstractControl = form.controls[field as string]
          return abstractControl.invalid && (abstractControl.touched || abstractControl.dirty || triedToSubmit)
        }),
      ),
    )
  }
}
